import { Schema } from "effect"
import { UsageProvider, Visibility } from "./common"
import { BriefSections } from "./domain/brief-sections"
import { ItemStatus } from "./domain/item"
import { Mind } from "./domain/org"
import { BRIEF_MAX_CHARS, ProjectRole } from "./domain/workspace"
import {
  AgentId,
  CommandId,
  Handle,
  ItemId,
  Markdown,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  ShortRef,
  Slug,
  TypeKey,
  UserId,
  WorkerRef,
  WorkspaceId,
} from "./ids"
import { RunKind, RunPhase } from "./run"
import { ProfileAccess, RunnerServes, RunnerSharedProfile } from "./runner"
import { AgentPresence } from "./state"
import { BillingClass, DayString } from "./usage"

// #region http
/**
 * HTTP bodies between the cloud tracks and the web (integration wave §6.1, decision 0011 P10). Responses are decoded
 * with these on both ends. Errors are `{ error: "<snake_case>", … }`; times are epoch ms.
 */

/** H1 `GET /api/workspaces/:ws/projects` → `{ projects: ProjectListItem[] }`, sorted by name. */
export const ProjectListItem = Schema.Struct({
  id: ProjectId,
  workspaceId: WorkspaceId,
  slug: Slug,
  name: Schema.String,
  status: Schema.Literals(["active", "paused", "archived"]),
  leadAgentId: Schema.NullOr(AgentId),
  repoIds: Schema.Array(RepoId),
  createdBy: UserId,
  createdAt: Millis,
  updatedAt: Millis,
  /** Decision 0129: `private` (members only) or `workspace` (every workspace member sees it). Absent = workspace. */
  visibility: Schema.optionalKey(Visibility),
  /**
   * Decision 0129: what the caller may do here: `admin` (project admin, or a workspace owner/admin), `member`, or
   * `reader` (a workspace-visible project the caller isn't a member of: they may read it and Join). Absent = member.
   */
  access: Schema.optionalKey(Schema.Literals(["admin", "member", "reader"])),
})
export type ProjectListItem = typeof ProjectListItem.Type
export type ProjectAccess = NonNullable<ProjectListItem["access"]>

/** Decision 0129: `PUT /api/workspaces/:ws/projects/:projectId/members/:userId` (add a workspace member, or set a role). */
export const SetProjectMemberBody = Schema.Struct({ role: ProjectRole })
export type SetProjectMemberBody = typeof SetProjectMemberBody.Type

/** The lead's charter (W24); the default is T1's lead charter, which the web shows prefilled. */
export const LeadCharterBody = Schema.Struct({
  mission: Schema.String.check(Schema.isMaxLength(280)),
  body: Markdown.check(Schema.isMaxLength(4000)),
})

/** H2 `POST /api/workspaces/:ws/projects`. `commandId` is the idempotency key of the whole create. */
export const CreateProjectBody = Schema.Struct({
  commandId: CommandId,
  /** 1–80 characters after trimming. */
  name: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  /** Default: slugify(name), with -2, -3… on collision; an explicit taken slug → 409 slug_taken. */
  slug: Schema.optionalKey(Slug),
  /** The brief (decision 0049): vision, for whom, principles, not doing, how to build and test. In every pack. */
  description: Schema.optionalKey(Markdown.check(Schema.isMaxLength(BRIEF_MAX_CHARS))),
  /** 0–5 repos of this workspace. */
  repoIds: Schema.Array(RepoId).check(Schema.isMaxLength(5)),
  lead: Schema.Struct({
    name: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(40)),
    handle: Handle,
    mind: Mind,
    charter: Schema.optionalKey(LeadCharterBody),
  }),
  /** Decision 0129: default `workspace`. The creator is the project's first admin. */
  visibility: Schema.optionalKey(Visibility),
})
export type CreateProjectBody = typeof CreateProjectBody.Type

/** Decision 0166 §4: a name and handle for an agent, free in the workspace. */
export const SuggestedAgentName = Schema.Struct({ name: Schema.String, handle: Schema.String })

/** Decision 0166 §4: why a name or handle can't be used (H2's `409 lead_name_taken` carries it as `problem`). */
export const AgentNameProblem = Schema.Struct({
  field: Schema.Literals(["name", "handle"]),
  message: Schema.String,
  suggestion: SuggestedAgentName,
})

/**
 * Decision 0166 §4, `GET /api/workspaces/:ws/agent-name?name=&handle=&agent=&seed=`: a free name for a new lead
 * (`seed` varies it, so a sheet keeps one suggestion while it is open), and what is wrong with the asked name and handle
 * (null when free, or nothing asked). With `agent` and that agent's own name and handle: its rename hint when another
 * agent or person of the workspace has the same (existing duplicates are kept).
 */
export const AgentNameAnswer = Schema.Struct({
  suggestion: SuggestedAgentName,
  problem: Schema.NullOr(AgentNameProblem),
})
export type AgentNameAnswer = typeof AgentNameAnswer.Type

/** H3/H4: a repo of the workspace's registry (D1 `repo`). `remote` is normalized (core normalizeRemote). */
export const RepoView = Schema.Struct({
  id: RepoId,
  remote: Schema.String,
  defaultBranch: Schema.String,
  createdBy: UserId,
  createdAt: Millis,
  /** Settings wave: last change of the registry row. */
  updatedAt: Schema.optionalKey(Millis),
})
export type RepoView = typeof RepoView.Type

/** H4 `POST /api/workspaces/:ws/repos`. `remote`: any git URL or host/org/repo. */
export const CreateRepoBody = Schema.Struct({
  remote: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(500)),
  defaultBranch: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(255)),
})
export type CreateRepoBody = typeof CreateRepoBody.Type

/** H5 `GET /api/workspaces/:ws/minds`: both providers, in this order. */
export const PlatformMinds = Schema.Struct({
  platform: Schema.Array(
    Schema.Struct({
      provider: Schema.Literals(["anthropic", "openai"]),
      /** A workspace key (valid, untested or unreachable) or the operator key is set (settings wave H5). */
      available: Schema.Boolean,
      defaultModel: Schema.String,
      /** Where the key comes from; null = none. */
      source: Schema.optionalKey(Schema.NullOr(Schema.Literals(["workspace", "operator"]))),
      /** The workspace key's models from its last test ([] otherwise). */
      models: Schema.optionalKey(Schema.Array(Schema.String)),
    }),
  ),
})
export type PlatformMinds = typeof PlatformMinds.Type

/** H7 `POST /api/runners/pair/poll`, the approved body. */
export const PairApproved = Schema.Struct({
  status: Schema.Literal("approved"),
  runnerId: RunnerId,
  runnerToken: Schema.String,
  workspaceId: WorkspaceId,
  workspaceName: Schema.String,
  /** The legacy JSON socket (unchanged). */
  connectUrl: Schema.String,
  /** wss://<host>/api/runners/rpc */
  rpcUrl: Schema.String,
  signerKeys: Schema.Array(
    Schema.Struct({
      kid: Schema.String,
      /** base64url raw Ed25519 public key (32 bytes). */
      publicKey: Schema.String,
      notAfter: Millis,
    }),
  ),
})
export type PairApproved = typeof PairApproved.Type

/** One registered repo as a v1 runner advertises it (H8). `pushReady` comes from the console manifest's probe. */
export const RunnerRepoView = Schema.Struct({
  repoId: RepoId,
  remote: Schema.String,
  defaultBranch: Schema.String,
  pushReady: Schema.NullOr(Schema.Boolean),
})

/** One live lease of a runner, for "Running now" on the runner card (fix wave §4.4). Display only. */
export const RunnerLeaseView = Schema.Struct({
  runId: RunId,
  runRef: ShortRef,
  kind: RunKind,
  projectId: Schema.NullOr(ProjectId),
  projectName: Schema.NullOr(Schema.String),
  /** From the hub label's `taskRef`/`taskTitle` (D7: the hub's names stay this release). */
  itemId: Schema.NullOr(ItemId),
  itemRef: Schema.NullOr(ShortRef),
  itemTitle: Schema.NullOr(Schema.String),
  agentId: Schema.NullOr(AgentId),
  profileId: ProfileId,
  phase: RunPhase,
  grantedAt: Millis,
  /** Settings wave: the temporary teammate ("W-3"). */
  workerRef: Schema.optionalKey(WorkerRef),
})
export type RunnerLeaseView = typeof RunnerLeaseView.Type

/**
 * H8 `GET /api/workspaces/:ws/runners[/:id]` items: the existing RunnerListItem fields (the console's runner view
 * plus release info; the nested console parts stay display JSON) and, optional for older clients, `protocol`,
 * `repos` and `activeRuns`. Unknown extra fields are ignored when decoding.
 */
export const RunnerListItemV1 = Schema.Struct({
  runnerId: RunnerId,
  name: Schema.String,
  os: Schema.String,
  arch: Schema.String,
  version: Schema.String,
  ownerUserId: UserId,
  status: Schema.Literals(["online", "offline"]),
  lastSeenAt: Schema.NullOr(Schema.Number),
  manifest: Schema.NullOr(Schema.Json),
  desiredConfig: Schema.NullOr(Schema.Json),
  pendingUpdate: Schema.NullOr(Schema.Json),
  updateStatus: Schema.NullOr(Schema.Json),
  latestVersion: Schema.NullOr(Schema.String),
  updateAvailable: Schema.Boolean,
  protocol: Schema.optionalKey(Schema.Union([Schema.Literal("legacy"), Schema.Literal(1)])),
  repos: Schema.optionalKey(Schema.Array(RunnerRepoView)),
  activeRuns: Schema.optionalKey(NonNegInt),
  /** The runner's live leases (fix wave §4.4). */
  leases: Schema.optionalKey(Schema.Array(RunnerLeaseView)),
  /** Settings wave: the scheduling manifest's `features` ([] when absent). */
  features: Schema.optionalKey(Schema.Array(Schema.String)),
  /**
   * Decision 0166 §1.3, §2: Just me / The team (the Platform Runner is the team's) and each profile's owner and scope,
   * so the web can tell which runners may host a lead and whom a turn may run for. Absent = `owner`, no explicit access.
   */
  serves: Schema.optionalKey(RunnerServes),
  profileAccess: Schema.optionalKey(Schema.Record(Schema.String, ProfileAccess)),
  /**
   * Decision 0168: someone else's personal machine (`manifest: null`): the logins on it the viewer may use (shared with
   * the workspace or some projects, or their own), so the web's reach agrees with placement. Absent from older servers.
   */
  redacted: Schema.optionalKey(Schema.Boolean),
  sharedProfiles: Schema.optionalKey(Schema.Array(RunnerSharedProfile)),
})
export type RunnerListItemV1 = typeof RunnerListItemV1.Type

/** H10 `GET /api/workspaces/:ws/needs-you` (W23). Items are in Needs-you order, at most 50. */
export const NeedsYouSummary = Schema.Struct({
  count: NonNegInt,
  items: Schema.Array(
    Schema.Struct({
      projectId: ProjectId,
      projectName: Schema.String,
      proposalId: ProposalId,
      kind: Schema.String,
      oneLine: Schema.String,
      /** The row's words, built with the card rules ("LEA-9 is stuck: T-I report"); absent from older servers. */
      title: Schema.optionalKey(Schema.String),
      /** The card's kind tag: plan · brief · stuck · review · question · permission · memory · outward. */
      card: Schema.optionalKey(Schema.String),
      tier: Schema.String,
      decideBy: Schema.NullOr(Millis),
    }),
  ).check(Schema.isMaxLength(50)),
  /**
   * Decision 0163: items whose next move is the person's (core `needsYouOf`), oldest first, at most 50; absent from
   * older servers. `count` counts these too: it is the sum of the projects' Needs-you counts.
   */
  tasks: Schema.optionalKey(
    Schema.Array(
      Schema.Struct({
        projectId: ProjectId,
        projectName: Schema.String,
        itemId: ItemId,
        ref: Schema.String,
        title: Schema.String,
        /** The Needs-you group: answer · do · sort. */
        verb: Schema.String,
        /** do · close · answer · stuck · lend · sort (core `NeedsYouItemWhy`). */
        why: Schema.String,
        /** When it became the person's move. */
        since: Millis,
      }),
    ).check(Schema.isMaxLength(50)),
  ),
  /** projectIds that did not answer within their budget. */
  partial: Schema.Array(ProjectId),
})
export type NeedsYouSummary = typeof NeedsYouSummary.Type

// ---- settings wave §6 (moved from the earlier settings/home spec's provisional copies) ----

/** The highest role in Better Auth's comma list (owner > admin > member). */
export const WorkspaceRole = Schema.Literals(["owner", "admin", "member"])
export type WorkspaceRole = typeof WorkspaceRole.Type

/** H11 `GET /api/me/workspaces` items. */
export const MyWorkspace = Schema.Struct({
  id: WorkspaceId,
  name: Schema.String,
  slug: Schema.String,
  logo: Schema.NullOr(Schema.String),
  role: WorkspaceRole,
  /** role is owner and no other member of this workspace is an owner. */
  soleOwner: Schema.Boolean,
  joinedAt: Millis,
})
export type MyWorkspace = typeof MyWorkspace.Type
export const MyWorkspaces = Schema.Struct({
  workspaces: Schema.Array(MyWorkspace),
  activeWorkspaceId: Schema.NullOr(WorkspaceId),
})
export type MyWorkspaces = typeof MyWorkspaces.Type

/** H12 `GET /api/me/invitations` items. */
export const MyInvitation = Schema.Struct({
  id: Schema.String,
  workspace: Schema.Struct({
    id: WorkspaceId,
    name: Schema.String,
    slug: Schema.String,
    logo: Schema.NullOr(Schema.String),
  }),
  role: WorkspaceRole,
  inviterName: Schema.String,
  createdAt: Millis,
  expiresAt: Millis,
})
export type MyInvitation = typeof MyInvitation.Type
export const MyInvitations = Schema.Struct({ invitations: Schema.Array(MyInvitation) })
export type MyInvitations = typeof MyInvitations.Type

/** H13 `GET /api/invitations/:id/preview` (no auth): workspace details only while pending. */
export const InvitationPreview = Schema.Union([
  Schema.Struct({
    id: Schema.String,
    status: Schema.Literal("pending"),
    workspace: Schema.Struct({ name: Schema.String, logo: Schema.NullOr(Schema.String) }),
    inviterName: Schema.String,
    role: WorkspaceRole,
    /** First char + "…@" + domain, e.g. "m…@every.to". */
    emailMasked: Schema.String,
    /**
     * Decision 0130: the invited address in full, only when the request carried the link's key (`?k=`, the emailed
     * link): sign-up fills it in and locks it.
     */
    email: Schema.optionalKey(Schema.String),
    /**
     * Decision 0159: the projects the invitation names (not archived), by name, so the page says what the person
     * joins ("Join jung.dev to work on dictly.app"). Like the workspace name, with or without the link's key.
     */
    projects: Schema.optionalKey(Schema.Array(Schema.String)),
    expiresAt: Millis,
  }),
  Schema.Struct({
    id: Schema.String,
    status: Schema.Literals(["expired", "accepted", "rejected", "canceled"]),
  }),
])
export type InvitationPreview = typeof InvitationPreview.Type

/**
 * Decision 0130: `GET /api/me/invitations/:invitationId/landing` after Join: the project to open (the first project
 * the invitation named that the person is now in), else null (workspace Home).
 */
export const InvitationLanding = Schema.Struct({
  workspaceId: WorkspaceId,
  projectId: Schema.NullOr(ProjectId),
})
export type InvitationLanding = typeof InvitationLanding.Type

export const KeyStatus = Schema.Literals(["valid", "rejected", "no_access", "unreachable", "untested"])
export type KeyStatus = typeof KeyStatus.Type

/** H15: a workspace's key for one provider. Metadata only; the key itself never leaves the Workspace DO. */
export const ModelKey = Schema.Struct({
  id: Schema.String,
  provider: Schema.Literals(["anthropic", "openai"]),
  /** "sk-ant-…a4f2": a known prefix, "…" and the last 4. */
  hint: Schema.String,
  status: KeyStatus,
  modelCount: Schema.NullOr(NonNegInt),
  /** Model ids from the last successful test, newest first, at most 100 (settings wave H15). */
  models: Schema.Array(Schema.String).check(Schema.isMaxLength(100)),
  testedAt: Schema.NullOr(Millis),
  createdBy: Schema.String,
  createdAt: Millis,
  updatedBy: Schema.String,
  updatedAt: Millis,
})
export type ModelKey = typeof ModelKey.Type

export const KeyTest = Schema.Struct({
  result: Schema.Literals(["valid", "rejected", "no_access", "unreachable"]),
  httpStatus: Schema.NullOr(Schema.Int),
  modelCount: Schema.NullOr(NonNegInt),
  testedAt: Millis,
})
export type KeyTest = typeof KeyTest.Type

/** H15 `GET …/model-keys`: always both providers, anthropic first. */
export const ModelKeysList = Schema.Struct({
  providers: Schema.Array(
    Schema.Struct({ provider: Schema.Literals(["anthropic", "openai"]), key: Schema.NullOr(ModelKey) }),
  ),
})
export type ModelKeysList = typeof ModelKeysList.Type

/** H15 `PUT …/model-keys/:provider`. The key: trimmed, 20–512 printable ASCII characters without spaces. */
export const PutModelKeyBody = Schema.Struct({
  key: Schema.Trim.check(Schema.isMinLength(20), Schema.isMaxLength(512), Schema.isPattern(/^[\x21-\x7E]+$/)),
  force: Schema.optionalKey(Schema.Boolean),
})
export type PutModelKeyBody = typeof PutModelKeyBody.Type
/** H15 PUT and POST …/test, 200. */
export const ModelKeyResult = Schema.Struct({ key: ModelKey, test: KeyTest })
export type ModelKeyResult = typeof ModelKeyResult.Type

/** A runner's name: trimmed, 1–64 characters, no control characters. */
export const RunnerName = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(64),
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this rejects
  Schema.makeFilter((s: string) => !/[\u0000-\u001f\u007f]/.test(s) || "no control characters"),
)

/**
 * H16 `PATCH …/runners/:runnerId` (wave E, decision 0063): rename and/or switch Just me / The team; at least one key.
 * Only the owner may change `serves`.
 */
export const RunnerPatchBody = Schema.Struct({
  name: Schema.optionalKey(RunnerName),
  serves: Schema.optionalKey(RunnerServes),
}).check(
  Schema.makeFilter((b) => b.name !== undefined || b.serves !== undefined || "name or serves is required"),
)
export type RunnerPatchBody = typeof RunnerPatchBody.Type

/** The pre-E body of H16 (name only). Kept so old clients and tests decode; a subset of `RunnerPatchBody`. */
export const RenameRunnerBody = Schema.Struct({ name: RunnerName })
export type RenameRunnerBody = typeof RenameRunnerBody.Type

/** Home: one goal of a project card. `judged`: a goal a person judges (target null; was `milestone`). */
export const GoalSummary = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("metric"),
    id: Schema.String,
    ref: Schema.String,
    title: Schema.String,
    current: Schema.NullOr(Schema.Number),
    target: Schema.Number,
    unit: Schema.NullOr(Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("judged"),
    id: Schema.String,
    ref: Schema.String,
    title: Schema.String,
    dueAt: Schema.NullOr(Millis),
    /** Top-level items serving it: done of total (cancelled left out). */
    serving: Schema.Struct({ done: NonNegInt, total: NonNegInt }),
  }),
])
export type GoalSummary = typeof GoalSummary.Type

/** `ProjectDO.summary(userId)`: live numbers of one project for home (settings wave §6.2). */
export const ProjectLiveSummary = Schema.Struct({
  /** Open proposals this user may decide. */
  needsYou: NonNegInt,
  /** At most 2, by rank. */
  goals: Schema.Array(GoalSummary),
  moreGoals: NonNegInt,
  counts: Schema.Struct({
    areas: NonNegInt,
    running: NonNegInt,
    blocked: NonNegInt,
    waitingForRunner: NonNegInt,
    temporaryWorkers: NonNegInt,
  }),
  /** P2: the My work badge for this user (Ready for you + Ready to close). */
  myWork: Schema.optionalKey(NonNegInt),
  /** state from core presence (never "working"). */
  lead: Schema.NullOr(
    Schema.Struct({
      agentId: AgentId,
      name: Schema.String,
      state: AgentPresence.fields.state,
      mind: Mind,
      /**
       * Decision 0168: the lead's keeper, so Home reads the viewer's reach (whether their message could run) as the
       * project page does; and whether its newest turn failed ("last turn failed", not "idle"). Absent from older servers.
       */
      ownerId: Schema.optionalKey(UserId),
      lastTurnFailed: Schema.optionalKey(Schema.Boolean),
    }),
  ),
  /**
   * Wave E (decision 0063, spec §7.5): every active agent pinned to a runner (`mind.requires.runner`), so a machine's
   * card can say "Lea and Eda think here". Absent from servers before E.
   */
  pinned: Schema.optionalKey(
    Schema.Array(Schema.Struct({ agentId: AgentId, name: Schema.String, runnerId: Schema.String })),
  ),
  /** Decision 0130: someone wrote its overview (not the pages job's template): Connect hides "Onboard <project>". */
  onboarded: Schema.optionalKey(Schema.Boolean),
  /**
   * Onboarding week A4: the project has a plan (an item that isn't cancelled). Home's card says "Open the chat with
   * Lea" instead of "Tell Lea what you want to build" then. Absent from older servers.
   */
  planned: Schema.optionalKey(Schema.Boolean),
  /**
   * Decision 0166 §5: the lead's chat or the plan has content (a turn someone asked, a card): Home stops saying "Tell
   * Lea what you want to build". Absent from older servers.
   */
  chatted: Schema.optionalKey(Schema.Boolean),
  /**
   * Decision 0130: the viewer has asked something here (a turn they asked, an item they created): a member's "Say hi
   * to <lead>" step is done.
   */
  youWrote: Schema.optionalKey(Schema.Boolean),
  /** Decision 0098, 0130: the lead's newest brief to the viewer (Home shows it). Absent from older servers and when none. */
  brief: Schema.optionalKey(
    Schema.Struct({
      agentId: AgentId,
      name: Schema.String,
      handle: Schema.String,
      to: UserId,
      body: Schema.String,
      at: Millis,
      /** Decision 0144: the brief's sections, rendered live on Home. Absent: a markdown-only brief. */
      sections: Schema.optionalKey(BriefSections),
    }),
  ),
})
export type ProjectLiveSummary = typeof ProjectLiveSummary.Type

export const ProjectSummary = Schema.Struct({
  id: ProjectId,
  name: Schema.String,
  slug: Schema.String,
  status: Schema.Literals(["active", "paused", "archived"]),
  repoIds: Schema.Array(RepoId),
  /** live.lead.agentId when `live` is present (authoritative), else the D1 index value (best effort). */
  leadAgentId: Schema.NullOr(AgentId),
  /** Active and paused projects (≤ 20, 2 s budget each); null for archived ones, over the cap, or on a miss. */
  live: Schema.NullOr(ProjectLiveSummary),
  /** Decision 0129: as on ProjectListItem. */
  visibility: Schema.optionalKey(Visibility),
  /** Decision 0129: as on ProjectListItem (`reader`: a workspace project the caller may Join). */
  access: Schema.optionalKey(Schema.Literals(["admin", "member", "reader"])),
})
export type ProjectSummary = typeof ProjectSummary.Type

/** H14 `GET /api/workspaces/:ws/home`. */
export const HomeData = Schema.Struct({
  workspace: Schema.Struct({
    id: WorkspaceId,
    name: Schema.String,
    slug: Schema.String,
    logo: Schema.NullOr(Schema.String),
    createdAt: Millis,
  }),
  me: Schema.Struct({ userId: UserId, role: WorkspaceRole, soleOwner: Schema.Boolean }),
  /** pendingInvitations: null unless the caller is an owner or admin. */
  members: Schema.Struct({ count: NonNegInt, pendingInvitations: Schema.NullOr(NonNegInt) }),
  runners: Schema.Struct({
    total: NonNegInt,
    online: NonNegInt,
    mine: NonNegInt,
    updateAvailable: NonNegInt,
  }),
  modelKeys: Schema.Struct({ anthropic: Schema.NullOr(KeyStatus), openai: Schema.NullOr(KeyStatus) }),
  /** Every project, sorted by name, ≤ 100. */
  projects: Schema.Array(ProjectSummary),
  needsYou: NeedsYouSummary,
  /** Reserved; empty this wave. */
  activity: Schema.Array(Schema.Unknown),
  generatedAt: Millis,
})
export type HomeData = typeof HomeData.Type

// ---- work-model cutover (decisions 0035, 0036) ----

/** My work sections (core `myWork`): items where the user is the person assignee, or the person owner of a container. */
export const MyWorkSection = Schema.Literals([
  "ready",
  "waiting_on_you",
  "in_progress",
  "ready_to_close",
  "later",
])
export type MyWorkSection = typeof MyWorkSection.Type

/** One My work row (§5.4). Derived on read; nothing is stored. */
export const MyWorkRow = Schema.Struct({
  projectId: ProjectId,
  itemId: ItemId,
  ref: ShortRef,
  title: Schema.String,
  type: TypeKey,
  status: ItemStatus,
  section: MyWorkSection,
  /** Open leaves whose frontier includes this item. */
  holds: NonNegInt,
  /** Up to 3 refs it unblocks. */
  unblocks: Schema.Array(ShortRef),
  dueAt: Schema.NullOr(Millis),
  overdue: Schema.Boolean,
  notBefore: Schema.NullOr(Millis),
  /** "waiting on W-102's fix" (later rows). */
  waitingOn: Schema.NullOr(Schema.String),
  /** The description changed by someone else in the last 3 days. */
  specChanged: Schema.NullOr(Schema.Struct({ from: PosInt, to: PosInt, by: Schema.String, at: Millis })),
})
export type MyWorkRow = typeof MyWorkRow.Type

/** Per-project My work (`ProjectDO.myWork(userId)`); also each project's share of H19. */
export const ProjectMyWork = Schema.Struct({
  rows: Schema.Array(MyWorkRow),
  /** The badge: Ready for you + Ready to close. */
  badge: NonNegInt,
})
export type ProjectMyWork = typeof ProjectMyWork.Type

/** H19 (P2) `GET /api/workspaces/:ws/my-work`: across projects, fan-out like H10. */
export const MyWorkSummary = Schema.Struct({
  rows: Schema.Array(MyWorkRow).check(Schema.isMaxLength(100)),
  counts: Schema.Struct({
    ready: NonNegInt,
    waitingOnYou: NonNegInt,
    readyToClose: NonNegInt,
    overdue: NonNegInt,
  }),
  /** projectIds that did not answer within their budget. */
  partial: Schema.Array(ProjectId),
})
export type MyWorkSummary = typeof MyWorkSummary.Type

/** One stored record that neither decoder reads (H21): never the payload. */
export const DecodeFailure = Schema.Struct({
  kind: Schema.String,
  tag: Schema.NullOr(Schema.String),
  seq: Schema.optionalKey(Seq),
  /** ≤ 300 characters. */
  message: Schema.String,
})
export type DecodeFailure = typeof DecodeFailure.Type

/**
 * H21 (P0) `GET /api/workspaces/:ws/projects/:projectId/decode-check` (owner/admin, read-only): every stored record of
 * the project's DOs through `decodeStored`. The go/no-go after the deploy: zero failures.
 */
export const DecodeCheckResult = Schema.Struct({
  projectId: ProjectId,
  lastSeq: Seq,
  /** Records decoded per kind (eventEnvelope, projectState, run, comment, commandResult, wakeReason, …). */
  counts: Schema.Record(Schema.String, NonNegInt),
  /** v1 records among them (upcast on read), per kind. */
  upcast: Schema.Record(Schema.String, NonNegInt),
  failures: Schema.Array(DecodeFailure),
  /** v2 readiness of every item, counted by tag. */
  readiness: Schema.Record(Schema.String, NonNegInt),
  /** The intended readiness differences on old data (§1.5): each only holds work v1 would have run. */
  intended: Schema.Struct({
    ancestorHolds: NonNegInt,
    g2Holds: NonNegInt,
    parentTasksWithOpenSubtasks: NonNegInt,
    emptyPlannable: NonNegInt,
  }),
})
export type DecodeCheckResult = typeof DecodeCheckResult.Type

/**
 * H22 (P0) `POST /api/workspaces/:ws/restore` (workspace owner only): Durable Object point-in-time recovery to `at`
 * (decision 0036, D15). Without `projectIds`: the Workspace DO, every Project DO, their agents' bodies and the Run DOs
 * of runs not ended at `at`. With `projectIds`: only those Project DOs (the dry run). `confirm` must equal the
 * workspace id.
 */
export const RestoreBody = Schema.Struct({
  at: Millis,
  confirm: Schema.String,
  apply: Schema.Literals(["on_restart", "now"]),
  projectIds: Schema.optionalKey(Schema.Array(ProjectId)),
})
export type RestoreBody = typeof RestoreBody.Type
export const RestoreResult = Schema.Struct({
  restored: Schema.Array(
    Schema.Struct({
      /** "workspace", "project:<id>", "agent:<id>", "run:<id>". */
      name: Schema.String,
      restoredTo: Millis,
      /** The bookmark `onNextSessionRestoreBookmark` returned: restoring to it undoes the restore. Not a secret. */
      undoBookmark: Schema.String,
    }),
  ),
  /** Objects that could not be restored (the reply is then 207): name and a message of at most 300 characters. */
  failed: Schema.optionalKey(Schema.Array(Schema.Struct({ name: Schema.String, message: Schema.String }))),
})
export type RestoreResult = typeof RestoreResult.Type
// #endregion http

// #region usage-http
/**
 * Wave F (decision 0062): usage reads. H23 `GET /api/workspaces/:ws/projects/:projectId/usage` (any member; the project
 * must belong to :ws), H24 `GET /api/workspaces/:ws/usage` (any member; from the D1 ledger), H25 `GET /api/me/usage`
 * (only the signed-in person: their plans and keys across workspaces). Query: `from`, `to` (UTC days, inclusive, at most
 * 400 days apart), `by`, `billing` (comma-separated classes), `scope` (project route only: "item:<id>" | "agent:<id>").
 * Anything else is `400 invalid_query`. Money is micro-USD; every figure is an estimate, not an invoice.
 */
export const UsageGroupBy = Schema.Literals([
  "day",
  "asked_by",
  "paid_by",
  "agent",
  "model",
  "billing",
  "project",
  "item",
])
export type UsageGroupBy = typeof UsageGroupBy.Type

export const UsageQuery = Schema.Struct({
  from: DayString,
  to: DayString,
  by: UsageGroupBy,
  billing: Schema.optionalKey(Schema.Array(BillingClass)),
  /** Project endpoint only: "item:<id>" (the item and, for a top-level item, everything under it) | "agent:<id>". */
  scope: Schema.optionalKey(Schema.String),
})
export type UsageQuery = typeof UsageQuery.Type

/** One billing class inside a total. */
export const UsageBillingTotals = Schema.Struct({
  billing: BillingClass,
  listMicroUsd: NonNegInt,
  chargedMicroUsd: NonNegInt,
  runs: NonNegInt,
})

/** core `UsageTotals`: sums over normalized rows (input = uncached). `runs` counts each run once, on its primary row. */
export const UsageTotalsSchema = Schema.Struct({
  runs: NonNegInt,
  inputTokens: NonNegInt,
  cacheReadTokens: NonNegInt,
  cacheWriteTokens: NonNegInt,
  outputTokens: NonNegInt,
  /** ≈ at API list price, compute included; rows without a price add nothing here and count in `unpricedRows`. */
  listMicroUsd: NonNegInt,
  unpricedRows: NonNegInt,
  /** Money actually charged (keys, extra usage, vendor, compute); included subscription use adds nothing. */
  chargedMicroUsd: NonNegInt,
  /** Rows that cost money of an unknown amount (api_key / extra / vendor / compute with a null cost). */
  chargedUnknownRows: NonNegInt,
  /** The compute part of `listMicroUsd` (0 until platform runners). */
  computeMicroUsd: NonNegInt,
  byBilling: Schema.Array(UsageBillingTotals),
})
export type UsageTotalsSchema = typeof UsageTotalsSchema.Type

export const UsageGroup = Schema.Struct({
  key: Schema.String,
  /** Resolved by the cloud: member names, agent handles, plan labels, model ids, project names. */
  label: Schema.String,
  totals: UsageTotalsSchema,
  /** "Claude Max" for paid_by groups whose funding is a plan. */
  plan: Schema.optionalKey(Schema.String),
  /** `by=agent` only: her own turns, and her workers' and validators' runs. */
  own: Schema.optionalKey(UsageTotalsSchema),
  workers: Schema.optionalKey(UsageTotalsSchema),
})
export type UsageGroup = typeof UsageGroup.Type

export const UsageReport = Schema.Struct({
  from: DayString,
  to: DayString,
  by: UsageGroupBy,
  priceTableVersion: Schema.String,
  totals: UsageTotalsSchema,
  /** Sorted by listMicroUsd, descending. */
  groups: Schema.Array(UsageGroup),
  days: Schema.Array(Schema.Struct({ day: DayString, totals: UsageTotalsSchema })),
  /** The workspace ledger is still backfilling older runs (first read after the deploy). */
  partial: Schema.Boolean,
})
export type UsageReport = typeof UsageReport.Type

export const MyUsageReport = Schema.Struct({
  from: DayString,
  to: DayString,
  plans: Schema.Array(
    Schema.Struct({
      /** core `planLabel` ("Claude Max"); null for a key. */
      plan: Schema.NullOr(Schema.String),
      kind: Schema.Literals(["plan", "key"]),
      /** `google` (decision 0109): an Antigravity login; `meta` (decision 0110): a Muse Code login. */
      provider: UsageProvider,
      /** core `planSeat`: "org" seats are listed apart ("Seats your organisation pays for"); keys are "personal". */
      seat: Schema.Literals(["personal", "org", "unknown"]),
      workspaces: Schema.Array(
        Schema.Struct({
          /** null: a workspace the person is no longer a member of ("Another workspace"). */
          workspaceId: Schema.NullOr(Schema.String),
          name: Schema.String,
          totals: UsageTotalsSchema,
        }),
      ),
      totals: UsageTotalsSchema,
    }),
  ),
  days: Schema.Array(Schema.Struct({ day: DayString, totals: UsageTotalsSchema })),
})
export type MyUsageReport = typeof MyUsageReport.Type
// #endregion usage-http
